/**
 * ============================================================================
 * MODAL COMPONENT - 100% Ready with Animations
 * ============================================================================
 */

import React, { useEffect } from 'react';

/**
 * Modal Component
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.title - Modal title
 * @param {string} props.size - Modal size (sm, md, lg, xl, full)
 * @param {boolean} props.showCloseButton - Show close button
 * @param {React.ReactNode} props.footer - Footer content
 * @param {string} props.className - Additional classes
 * @returns {JSX.Element}
 */
const Modal = ({
  isOpen,
  onClose,
  children,
  title,
  size = 'md',
  showCloseButton = true,
  footer,
  className = '',
}) => {
  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      {/* Backdrop - LinkedIn/Cursor: blur */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[4px] animate-fadeIn"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal - dark surface by default */}
      <div
        className={`
          relative z-10
          bg-[var(--color-surface-raised)] text-[var(--color-text-primary)]
          border border-[var(--color-border)] rounded-xl
          shadow-[0_20px_40px_rgba(0,0,0,0.4)]
          w-full ${sizeClasses[size] || sizeClasses.md}
          max-h-[90vh] overflow-hidden
          animate-scaleIn
          ${className}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)]">
            {title && (
              <h2
                id="modal-title"
                className="text-xl font-semibold text-[var(--color-text-primary)]"
              >
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="
                  p-2 rounded-md text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]
                  hover:bg-[var(--color-background-hover)] transition-all duration-200
                  focus:outline-none focus:ring-2 focus:ring-[#22c55e] focus:ring-offset-2 focus:ring-offset-[var(--color-bg-primary)]
                "
                aria-label="Close modal"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] text-[var(--color-text-primary)]">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-background-secondary)]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
